import { supabase } from './supabase'
import { base } from './db'
import type { DocumentChantier } from './types'

/**
 * Corbeille des documents (plans, devis, addenda) — administrateurs.
 *
 * « Retirer » un document le met à la corbeille : il est masqué pour toute
 * l'équipe (règle de lecture de la migration 008) mais rien n'est effacé.
 * Il se restaure d'un geste pendant 30 jours. Passé ce délai, il est
 * effacé pour de bon la prochaine fois qu'un administrateur ouvre l'onglet.
 */

export const JOURS_CORBEILLE = 30

export interface DocumentCorbeille extends DocumentChantier {
  supprime_le: string
  supprime_par: string | null
  supprimeur?: { nom: string | null } | null
}

/** Date à laquelle le document sera effacé pour de bon. */
export function dateEffacement(d: Pick<DocumentCorbeille, 'supprime_le'>): Date {
  return new Date(new Date(d.supprime_le).getTime() + JOURS_CORBEILLE * 86400000)
}

export async function mettreALaCorbeille(doc: Pick<DocumentChantier, 'id'>, profilId: string | null): Promise<void> {
  const { error } = await supabase
    .from('documents')
    .update({ supprime_le: new Date().toISOString(), supprime_par: profilId })
    .eq('id', doc.id)
  if (error) throw error
}

export async function restaurer(doc: Pick<DocumentChantier, 'id'>): Promise<void> {
  const { error } = await supabase.from('documents').update({ supprime_le: null, supprime_par: null }).eq('id', doc.id)
  if (error) throw error
}

/** Documents à la corbeille d'un chantier, pour les types donnés (plus récents d'abord). */
export async function chargerCorbeille(chantierId: string, types: DocumentChantier['type'][]): Promise<DocumentCorbeille[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('*, supprimeur:profils!documents_supprime_par_fkey(nom)')
    .eq('chantier_id', chantierId)
    .in('type', types)
    .not('supprime_le', 'is', null)
    .order('supprime_le', { ascending: false })
  if (error) throw error
  return (data as DocumentCorbeille[]) ?? []
}

/** Efface pour de bon les documents restés plus de 30 jours à la corbeille. */
export async function viderExpires(docs: DocumentCorbeille[]): Promise<number> {
  const maintenant = Date.now()
  let n = 0
  for (const d of docs) {
    if (dateEffacement(d).getTime() > maintenant) continue
    try {
      await supprimerDocument(d)
      n++
    } catch {
      // on réessaiera à la prochaine ouverture
    }
  }
  return n
}

/**
 * Suppression définitive (depuis la corbeille seulement).
 *
 * Efface, dans l'ordre :
 *  1. les pages découpées (<chantier>/pages/<document>/…) et le PDF d'origine ;
 *  2. la ligne `documents` — la base efface d'elle-même ce qui en dépend
 *     (index des pages, annotations, liens, texte du devis) ;
 *  3. les copies gardées sur cet appareil.
 *
 * Les fichiers sont effacés avant la ligne : si le stockage refuse, le
 * document reste dans la corbeille et on peut réessayer.
 */
export async function supprimerDocument(
  doc: Pick<DocumentChantier, 'id' | 'chantier_id' | 'fichier_path'>
): Promise<void> {
  const stockage = supabase.storage.from('documents')
  const dossier = `${doc.chantier_id}/pages/${doc.id}`

  const { data: pages, error: eListe } = await stockage.list(dossier, { limit: 10000 })
  if (eListe) throw eListe
  const chemins = (pages ?? []).map((f) => `${dossier}/${f.name}`)
  if (doc.fichier_path && !chemins.includes(doc.fichier_path)) chemins.push(doc.fichier_path)
  for (let i = 0; i < chemins.length; i += 500) {
    const { error } = await stockage.remove(chemins.slice(i, i + 500))
    if (error) throw error
  }

  const { error } = await supabase.from('documents').delete().eq('id', doc.id)
  if (error) throw error

  try {
    await base.plans.delete(doc.fichier_path)
    await base.plans.where('cle').startsWith(`${dossier}/`).delete()
    await base.annotations.where('document_id').equals(doc.id).delete()
    await base.liens.where('document_id').equals(doc.id).delete()
  } catch {
    // rien de grave : ces copies ne seront plus jamais lues
  }
}
